import * as THREE from 'three';

/**
 * streetcarSpline — CatmullRomCurve3 factory for the NAV-02 ride.
 *
 * Plan 05-01 Task 2 contract. Called from StreetcarRig (via useMemo keyed on
 * manifest.streetcarSpline.waypoints) so curve construction happens once per
 * manifest-load, not per frame.
 *
 * Config choices (per 05-RESEARCH.md Pattern 1 + Pitfall 1):
 *   - curveType='centripetal' — avoids loops/cusps when waypoints are
 *     unevenly spaced (the 8 Birmingham→Lone Pine Gap→lakeshore waypoints
 *     are NOT evenly spaced)
 *   - closed=false — it's a one-way ride, not a loop
 *   - tension=0.5 — standard "relaxed" curve; straighter through dense
 *     waypoints, bendier through sparse ones
 *
 * Arc-length uniformity is inherent: curve.getPointAt(t) internally uses a
 * u-to-t mapping table so the return is parameterized by arc length, not
 * raw parameter. StreetcarRig uses getPointAt (NOT getPoint) per Pitfall 1.
 */
export function buildStreetcarCurve(
  waypoints: [number, number, number][],
): THREE.CatmullRomCurve3 {
  if (waypoints.length < 2) {
    throw new Error(
      `buildStreetcarCurve: waypoints.length must be >= 2; got ${waypoints.length}`,
    );
  }
  const points = waypoints.map(([x, y, z]) => new THREE.Vector3(x, y, z));
  return new THREE.CatmullRomCurve3(
    points,
    /* closed */ false,
    /* curveType */ 'centripetal',
    /* tension */ 0.5,
  );
}

/**
 * DEFAULT_RIDE_DURATION_S — fallback when manifest.streetcarSpline.durationS
 * is absent. Matches the authored v3.2 world.json default (90s target for the
 * 60-120s NAV-02 window).
 */
export const DEFAULT_RIDE_DURATION_S = 90;
